import { useScorecardData, useUserPermissions } from 'hooks';
import { ScorecardStatus } from 'store/interfaces';
import { canDeleteOwnScorecard, getScorecardStates } from 'helpers';
import { useCallback } from 'react';
import { useScorecardContext } from 'contexts';
import usePagePermissions from './usePagePermissions';

export function useScorecardPermissionsFroStatusGetter() {
  const {
    hasFullAccess,
    isDepartmentOverviewPage,
    isOwnScorecardsPage,
    isMyTeamPage,
  } = usePagePermissions();
  const {
    canDeleteScorecards,
    canExportScorecards,
    canViewDepartmentOverview,
  } = useUserPermissions();
  const { isArchived, isDeleted: isCtxDeleted } = useScorecardContext();
  const isInactive = Boolean(isArchived || isCtxDeleted);

  return useCallback(
    (status: ScorecardStatus) => {
      const {
        isGoalsPrepared,
        isActive,
        isFeedbackCollected,
        isFeedbackRequested,
        isGoalsApproved,
        isCompleted,
        isDeleted,
      } = getScorecardStates(status);

      const canStopFeedbackGathering = hasFullAccess && isFeedbackRequested;
      const canEdit =
        (isActive && !isDepartmentOverviewPage) ||
        (isActive && isDepartmentOverviewPage && canViewDepartmentOverview) ||
        (isOwnScorecardsPage &&
          (isGoalsPrepared || isGoalsApproved || isFeedbackRequested)) ||
        (hasFullAccess &&
          (isGoalsPrepared ||
            isGoalsApproved ||
            isFeedbackRequested ||
            isFeedbackCollected ||
            isCompleted));
      // The owner can start feedback collection only once the goals are approved - from
      // goals_prepared it would skip the People Manager's approval.
      const canRequestFeedback =
        (hasFullAccess && (isGoalsPrepared || isGoalsApproved)) ||
        (isOwnScorecardsPage && isGoalsApproved);
      const canSubmitForReview = isActive && isOwnScorecardsPage;
      const canComplete =
        hasFullAccess && (isFeedbackRequested || isFeedbackCollected);
      const canGatherMoreFeedback =
        isDepartmentOverviewPage && hasFullAccess && isCompleted;
      const canApproveGoals = hasFullAccess && (isActive || isGoalsPrepared);
      // Reviewer-list edit power. Add-only in FR/FC (PM cannot deselect once
      // feedback collection has started); removal allowed in earlier states.
      const canEditReviewers = isActive || isGoalsPrepared || isGoalsApproved;
      // Reviewer Add button visibility.
      // Owner: active|goals_prepared|goals_approved|feedback_requested.
      // PM/Dept (`hasFullAccess`): same + feedback_collected.
      // Always hidden when completed, archived, or deleted.
      const canSetReviewers =
        !isInactive &&
        !isCompleted &&
        ((isOwnScorecardsPage &&
          (isActive ||
            isGoalsPrepared ||
            isGoalsApproved ||
            isFeedbackRequested)) ||
          (hasFullAccess &&
            (isActive ||
              isGoalsPrepared ||
              isGoalsApproved ||
              isFeedbackRequested ||
              isFeedbackCollected)));
      const canEditAllocatedProjects =
        isActive ||
        isGoalsPrepared ||
        isGoalsApproved ||
        (hasFullAccess && !isCompleted);
      const canDelete =
        ((isDepartmentOverviewPage && canViewDepartmentOverview) ||
          isMyTeamPage) &&
        canDeleteScorecards;
      // Permanent, and needs no delete permission - the owner is removing their own scorecard.
      const canDeleteOwn =
        !isInactive && canDeleteOwnScorecard({ status, isOwnScorecardsPage });
      const canRevertToActive =
        hasFullAccess &&
        (isGoalsPrepared ||
          isGoalsApproved ||
          isFeedbackRequested ||
          isFeedbackCollected);
      return {
        canExportScorecards: canExportScorecards && !isDeleted,
        canEdit,
        canRequestFeedback,
        canStopFeedbackGathering,
        canSubmitForReview,
        canComplete,
        canGatherMoreFeedback,
        canApproveGoals,
        canEditReviewers,
        canSetReviewers,
        canEditAllocatedProjects,
        canDelete,
        canDeleteOwn,
        canRevertToActive,
      };
    },
    [
      canExportScorecards,
      hasFullAccess,
      canDeleteScorecards,
      isDepartmentOverviewPage,
      isMyTeamPage,
      isOwnScorecardsPage,
      canViewDepartmentOverview,
      isInactive,
    ],
  );
}
export function useScorecardPermissions() {
  const { status } = useScorecardData();

  return useScorecardPermissionsFroStatusGetter()(status);
}
